/**
 * The Agents view of Ads: the weekly work as eight steps, each with what it did, what it may and may not do, and its last runs.
 *
 * Pure. The worker gathers a few facts from its own records and this turns them into the words on screen. Nothing here is
 * invented: a step with nothing behind it says so ("Starts once you approve"), and an unknown cost is "Not available".
 */
import { z } from "zod";
import { adProductIdSchema, type AdProductId } from "./adsProducts.ts";

export const AGENT_STEP_IDS = ["research", "learn", "ideas", "ads", "review", "file", "results", "advice"] as const;
export const agentStepIdSchema = z.enum(AGENT_STEP_IDS);
export type AgentStepId = (typeof AGENT_STEP_IDS)[number];

export const AGENT_STATES = ["done", "you", "waiting", "off", "idle"] as const;
export const agentStateSchema = z.enum(AGENT_STATES);
export type AgentState = (typeof AGENT_STATES)[number];

export const agentRunSchema = z.object({ at: z.string(), what: z.string(), ok: z.boolean() });
export type AgentRun = z.infer<typeof agentRunSchema>;

export const agentStepSchema = z.object({
  id: agentStepIdSchema,
  short: z.string(),
  title: z.string(),
  who: z.string(),
  state: agentStateSchema,
  did: z.string(),
  /** What this week's work cost, or "Not available". Empty for steps that cost nothing. */
  cost: z.string(),
  limits: z.array(z.string()),
  runs: z.array(agentRunSchema),
  /** Where a person goes to act on this step, when there is something to do. */
  action: z.object({ label: z.string(), to: z.enum(["review", "competitors", "results", "setup", "google", "run"]) }).nullable(),
});
export type AgentStep = z.infer<typeof agentStepSchema>;

export const weekRecapSchema = z.object({ ran: z.array(z.string()), next: z.array(z.string()) });
export type WeekRecap = z.infer<typeof weekRecapSchema>;

export const agentsViewSchema = z.object({
  product: adProductIdSchema,
  weekStart: z.string(),
  steps: z.array(agentStepSchema),
  /** What happened this week and what we will try next, in plain sentences. */
  recap: weekRecapSchema.default({ ran: [], next: [] }),
  /** The one thing waiting for a person, or null. */
  waiting: z.string().nullable(),
});
export type AgentsView = z.infer<typeof agentsViewSchema>;

export interface AgentFacts {
  product: AdProductId;
  weekStart: string;
  research: { runs: AgentRun[]; adsFound: number | null; costUsd: number | null; watching: number };
  learn: { verdicts: number; rulesOn: number; rulesOff: number };
  ideas: { written: number; picked: number; costUsd: number | null; runs: AgentRun[] };
  ads: { written: number; costUsd: number | null; runs: AgentRun[]; running: boolean };
  review: { waiting: number; approved: number; needsChanges: number; rejected: number };
  file: { readyToExport: number; exports: AgentRun[] };
  results: { googleConnected: boolean; googleLastAt: string | null; rowsThisWeek: number; rowsEver: number };
  advice: { open: number };
}

const money = (v: number | null) => (v === null ? "Not available" : `$${v.toFixed(2)}`);
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

export function buildAgentSteps(f: AgentFacts): AgentsView {
  const steps: AgentStep[] = [];

  steps.push({
    id: "research", short: "Find competitor ads", title: "Find competitor ads", who: "Researcher",
    state: f.research.watching === 0 ? "idle" : f.research.runs[0] && !f.research.runs[0].ok ? "you" : f.research.adsFound !== null ? "done" : "waiting",
    did: f.research.watching === 0 ? "No companies on your watchlist yet." : f.research.adsFound !== null ? `Found ${plural(f.research.adsFound, "ad")} on the last run.` : "Hasn't run yet.",
    cost: money(f.research.costUsd),
    limits: ["Looks at the companies on your watchlist", "US and Canada only", "Reads public ads. Never contacts a company"],
    runs: f.research.runs,
    action: f.research.watching === 0 ? { label: "Add a company", to: "competitors" } : null,
  });

  steps.push({
    id: "learn", short: "Learn from feedback", title: "Learn from your feedback", who: "Learner",
    state: f.learn.verdicts === 0 ? "idle" : "done",
    did: f.learn.verdicts === 0 ? "Starts once you review your first ads." : `${plural(f.learn.verdicts, "verdict")} read. ${plural(f.learn.rulesOn, "rule")} on${f.learn.rulesOff ? `, ${f.learn.rulesOff} off` : ""}.`,
    cost: "",
    limits: ["Reads the last 8 weeks of verdicts", "Never changes your verdicts", "You can switch any rule off"],
    runs: [],
    action: null,
  });

  steps.push({
    id: "ideas", short: "Write ideas", title: "Write ideas", who: "Idea writer",
    state: f.ideas.written === 0 ? "waiting" : "done",
    did: f.ideas.written === 0 ? "No ideas this week yet." : `${plural(f.ideas.written, "idea")} written this week. ${f.ideas.picked} picked.`,
    cost: money(f.ideas.costUsd),
    limits: ["Each idea needs one hard fact as proof", "Only facts from the Brand kit", "Ideas you said \"Not for us\" to are not repeated"],
    runs: f.ideas.runs,
    action: null,
  });

  steps.push({
    id: "ads", short: "Write ads and pictures", title: "Write ads and pictures", who: "Ad writer",
    state: f.ads.running ? "waiting" : f.ads.written === 0 ? "waiting" : "done",
    did: f.ads.running ? "Writing now…" : f.ads.written === 0 ? "No ads written this week yet." : `${plural(f.ads.written, "ad")} written this week.`,
    cost: money(f.ads.costUsd),
    limits: ["Uses only facts from the Brand kit", "Every ad is checked against the rules before you see it", "Never sends anything live"],
    runs: f.ads.runs,
    action: { label: "Write ads now", to: "run" },
  });

  steps.push({
    id: "review", short: "You review", title: "You review", who: "You",
    state: f.review.waiting > 0 ? "you" : f.review.approved + f.review.needsChanges + f.review.rejected > 0 ? "done" : "idle",
    did: f.review.waiting > 0 ? `${plural(f.review.waiting, "ad")} waiting for you. Nothing goes live without your OK.` : f.review.approved + f.review.needsChanges + f.review.rejected > 0 ? `${f.review.approved} approved, ${f.review.needsChanges} need changes, ${f.review.rejected} rejected.` : "Nothing to review yet.",
    cost: "",
    limits: ["Approve, ask for changes, or reject", "Your verdicts teach the Learner"],
    runs: [],
    action: f.review.waiting > 0 ? { label: "Start review", to: "review" } : null,
  });

  steps.push({
    id: "file", short: "Make Google file", title: "Make the Google Ads file", who: "Publisher",
    state: f.file.readyToExport > 0 ? "you" : f.file.exports.length > 0 ? "done" : "waiting",
    did: f.file.readyToExport > 0 ? `${plural(f.file.readyToExport, "approved ad")} ready for a file.` : f.file.exports.length > 0 ? "Last file made. Ads arrive paused." : "Starts once you approve Google ads.",
    cost: "",
    limits: ["Only approved ads go in", "Checks character limits again", "Never changes campaigns. Everything arrives paused"],
    runs: f.file.exports,
    action: null,
  });

  steps.push({
    id: "results", short: "Read results", title: "Read results", who: "Reporter",
    state: f.results.googleConnected || f.results.rowsEver > 0 ? "done" : "off",
    did: f.results.googleConnected ? (f.results.googleLastAt ? `Google results received. ${plural(f.results.rowsThisWeek, "row")} this week.` : "Google is connected. Run the script once to send the first results.") : f.results.rowsEver > 0 ? `${plural(f.results.rowsThisWeek, "row")} this week, added by hand or from a file.` : "No results yet. Connect Google Ads to start.",
    cost: "",
    limits: ["Reads views, clicks and sign-ups each week", "Can't change anything in your ad accounts"],
    runs: [],
    action: f.results.googleConnected ? null : { label: "Connect Google Ads", to: "google" },
  });

  steps.push({
    id: "advice", short: "Suggest next steps", title: "Suggest next steps", who: "Advisor",
    state: f.results.rowsEver === 0 ? "idle" : f.advice.open > 0 ? "you" : "done",
    did: f.results.rowsEver === 0 ? "Starts once results arrive." : f.advice.open > 0 ? `${plural(f.advice.open, "thing")} need${f.advice.open === 1 ? "s" : ""} your decision.` : "Nothing needs a decision.",
    cost: "",
    limits: ["Suggests. You decide.", "Pause or Keep going is always your call"],
    runs: [],
    action: f.advice.open > 0 ? { label: "See what needs a decision", to: "results" } : null,
  });

  const waiting = f.review.waiting > 0 ? `${plural(f.review.waiting, "ad")} ${f.review.waiting === 1 ? "is" : "are"} waiting for your review` : f.advice.open > 0 ? `${plural(f.advice.open, "decision")} waiting for you` : null;
  return { product: f.product, weekStart: f.weekStart, steps, recap: { ran: [], next: [] }, waiting };
}

// --------------------------------------------------------------------------------------------------------------------
// What ran this week, and what we will try next
// --------------------------------------------------------------------------------------------------------------------

export interface RecapInput {
  facts: AgentFacts;
  /** The lessons learned from verdicts, with their switches. */
  rules: Array<{ text: string; on: boolean }>;
  /** Tests in flight, as read by the insight rules. */
  tests: Array<{ adName: string; status: "not_enough" | "too_close" | "ahead" | "behind"; reason: string }>;
  swap: { replaceName: string; reason: string } | null;
  /** "Monday" when the weekly run is on, else null. */
  nextRunDay: string | null;
}

const MAX_RECAP_LINES = 5;

/** Plain sentences only, taken from facts we hold. Nothing is guessed, and an empty week says so. */
export function buildWeekRecap(i: RecapInput): WeekRecap {
  const f = i.facts;
  const ran: string[] = [];
  if (f.research.adsFound !== null) ran.push(`Found ${plural(f.research.adsFound, "competitor ad")}`);
  if (f.ideas.written > 0) ran.push(`Wrote ${plural(f.ideas.written, "idea")}${f.ideas.picked > 0 ? `, picked ${f.ideas.picked}` : ""}`);
  if (f.ads.written > 0) ran.push(`Wrote ${plural(f.ads.written, "ad")}`);
  const decided = f.review.approved + f.review.needsChanges + f.review.rejected;
  if (decided > 0) {
    const parts = [f.review.approved > 0 && `approved ${f.review.approved}`, f.review.needsChanges > 0 && `asked for changes on ${f.review.needsChanges}`, f.review.rejected > 0 && `turned down ${f.review.rejected}`].filter(Boolean) as string[];
    ran.push(`You ${parts.length > 1 ? `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}` : parts[0]}`);
  }
  if (f.review.waiting > 0) ran.push(`${plural(f.review.waiting, "ad")} still waiting for you`);
  if (f.file.exports.some((e) => e.at.slice(0, 10) >= f.weekStart)) ran.push("Built a Google Ads file");
  if (f.results.rowsThisWeek > 0) ran.push(`Read ${plural(f.results.rowsThisWeek, "result row")}`);
  if (ran.length === 0) ran.push("Nothing has run this week yet");

  const next: string[] = [];
  if (f.advice.open > 0) next.push(`${plural(f.advice.open, "ad")} need${f.advice.open === 1 ? "s" : ""} your answer: pause or keep going`);
  for (const t of i.tests) {
    if (t.status === "not_enough") next.push(`Keep testing “${t.adName}”. ${t.reason}`);
    else if (t.status === "ahead") next.push(`Use what “${t.adName}” showed in the next ads. ${t.reason}`);
    else if (t.status === "behind") next.push(`Try something different from “${t.adName}”. ${t.reason}`);
  }
  if (i.swap) next.push(`Replace “${i.swap.replaceName}” with a fresh ad. ${i.swap.reason}`);
  for (const r of i.rules.filter((x) => x.on).slice(0, 2)) next.push(`Keep following your lesson: ${r.text}`);
  if (i.nextRunDay) next.push(`Write the next ideas and ads on ${i.nextRunDay}`);
  if (next.length === 0) next.push(f.results.rowsEver === 0 ? "Wait for the first results, then we suggest what to try" : "Nothing new to try yet");

  return { ran: ran.slice(0, MAX_RECAP_LINES), next: next.slice(0, MAX_RECAP_LINES) };
}
